import React, { useEffect, useState } from 'react';
import { Tree } from 'antd';
import type { DataNode, EventDataNode, TreeProps } from 'antd/es/tree';
import CoursePageItem from './CoursePageItem';
import { observer } from 'mobx-react';
import { TTreeItemDataViewModel, TTreeViewModel } from '../../../../services/models/CourseViewModels';
import IconButton from '@mui/material/IconButton/IconButton';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { checkNumberOfChildren, checkNumberOfParents } from '../../../../utils/courseDashboard/coureDashboardUtils';
import { useStore } from '../../../../store/store';
import { useSnackbar } from 'notistack';
import { convertToAntdTree, getExpandedKeys } from '../../../../utils/pages/convertToAntdTree';

type NodeDragEventParams<CustomTreeNode> = {
  event: React.DragEvent;
  node: CustomTreeNode;
};

export interface IPageItem extends DataNode {
  id: number;
  data: TTreeItemDataViewModel;
  isExpanded: boolean;
  children: IPageItem[];
}

interface CoursePageListProps {
  pages: TTreeViewModel[];
  handleNewPageItem: (item: IPageItem) => void;
  handlePastePage: (item: IPageItem) => void;
  handleDuplicatePageItem: (item: IPageItem) => void;
  handleCopyPageItem: (item: IPageItem, copyChildren: boolean) => void;
  height: number;
  image: boolean;
}

export default observer(function CoursePageList({ pages, handleNewPageItem, handlePastePage, handleDuplicatePageItem, handleCopyPageItem, height, image }: CoursePageListProps) {
  const [treeData, setTreeData] = useState<IPageItem[]>(convertToAntdTree(pages));
  const [expandedKeys, setExpandedKeys] = useState<string[]>(getExpandedKeys(treeData));
  const [draggingKey, setDraggingKey] = useState<string>('');
  const { enqueueSnackbar } = useSnackbar();
  const { courseStore, pageStore } = useStore();
  const { courseViewModel, movePage } = courseStore;
  const { setExpanded } = pageStore;

  const handleDrop: TreeProps['onDrop'] = (info) => {
    const infoDragNode = info.dragNode as EventDataNode<IPageItem>;
    const dropKey = info.node.key as string;
    const dragKey = infoDragNode.key as string;
    const dropPosition = info.dropPosition;
    const dropToGap = info.dropToGap;

    let destinationParentId = '0';
    let destinationIndex: undefined | number = undefined;

    if (info.node.key === '__drop_spacer__') return;

    const data = [...treeData]; // Clone the tree data to modify it safely

    // Utility to loop through tree and act on a specific key
    const loop = (data: IPageItem[], key: string, callback: (node: IPageItem, index: number, arr: IPageItem[]) => void) => {
      for (let i = 0; i < data.length; i++) {
        if (data[i].key === key) {
          return callback(data[i], i, data);
        }
        if (data[i].children) {
          loop(data[i].children as IPageItem[], key, callback);
        }
      }
    };

    // Remove dragNode from its current location and store it
    let dragNode: IPageItem | null = null;
    loop(data, dragKey, (node, index, arr) => {
      arr.splice(index, 1); // Remove node from its original position
      dragNode = node;
    });

    if (!dragNode) return;

    if (!dropToGap) {
      // Dropped inside a node – make it a **child** of the target node
      loop(data, dropKey, (node) => {
        node.children = node.children || [];

        const insertIndex = 0;

        node.children.splice(insertIndex, 0, dragNode!); // insert at index 0 or any logic
        destinationParentId = node.key.toString();
        destinationIndex = insertIndex;
      });
    } else if (dropPosition === 0 || dropPosition === -1) {
      // Dropped **before** the node (same level as dropKey)
      loop(data, dropKey, (node, index, arr) => {
        arr.splice(index, 0, dragNode!); // Inserts dragNode before the target node in its parent's children array
        destinationIndex = index;
        destinationParentId = node.data.parentCoursePageID?.toString() || '0';
      });
    } else {
      // Dropped **after** the node (same level as dropKey)
      loop(data, dropKey, (node, index, arr) => {
        arr.splice(index + 1, 0, dragNode!); // Inserts dragNode after the target node in its parent's children array
        destinationIndex = index + 1;
        destinationParentId = node.data.parentCoursePageID?.toString() || '0';
      });
    }

    // Determine source and destination from keys
    const coursePageID = infoDragNode.key.toString();

    // Check parent/child depth
    if (!destinationParentId) return; //no parent. TEMP

    const parentCount = checkNumberOfParents(data, destinationParentId);
    const childCount = checkNumberOfChildren(infoDragNode);

    if (parentCount + childCount < 3) {
      movePage(coursePageID, destinationParentId, destinationIndex);
      /*       if (document.startViewTransition) {
        document.startViewTransition(() => {
          setTreeData(data); // Apply the modified tree structure
        });
      } else { */
      // Fallback: no transition support
      setTreeData(data); // Apply the modified tree structure
      /* } */
    } else {
      enqueueSnackbar('Maximum indent level reached. Please paste at a higher level.', {
        variant: 'info',
        autoHideDuration: 5000,
      });
    }
  };

  const handleExpand = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, aPageID: string) => {
    e.stopPropagation();
    setTreeData((data) => data.map((node) => (node.key === aPageID ? { ...node, isExpanded: true } : node)));
    if (courseViewModel) {
      setExpanded(courseViewModel.courseID, aPageID, true);
    }

    setExpandedKeys((prevKeys) => {
      return prevKeys.includes(aPageID) ? prevKeys : [...prevKeys, aPageID];
    });
  };

  const handleExpandItem = (aInfo: { node: EventDataNode<IPageItem>; expanded: boolean; nativeEvent: MouseEvent }) => {
    const pageID = String(aInfo.node.key);

    setExpandedKeys((prevKeys) => {
      if (aInfo.expanded) {
        // Add the key if not already present
        return prevKeys.includes(pageID) ? prevKeys : [...prevKeys, pageID];
      } else {
        // Remove the key
        return prevKeys.filter((k) => k !== pageID);
      }
    });

    if (courseViewModel) {
      setExpanded(courseViewModel.courseID, pageID, aInfo.expanded);
    }
  };

  const [currentDragGhost, setCurrentDragGhost] = useState<HTMLElement>();
  const [dragOffset, setDragOffset] = useState({ x: 20, y: 20 });

  const handleDragStart = (aInfo: NodeDragEventParams<IPageItem>) => {
    setDraggingKey(String(aInfo.node.key));
    const dragGhost = document.createElement('span');
    aInfo.event.dataTransfer.setDragImage(dragGhost, 0, 0); //Hides original ghost image

    const currentElement = aInfo.event.target as HTMLDivElement;
    const dragPage = currentElement.querySelector('[data-drag]') as HTMLDivElement | null;
    if (!dragPage) return;
    const cloneEl = dragPage.cloneNode(true) as HTMLElement;
    console.log(cloneEl);

    const bodyRect = document.body.getBoundingClientRect();
    const rect = dragPage.getBoundingClientRect();
    const offsetX = aInfo.event.clientX - (rect.x - bodyRect.left);
    const offsetY = aInfo.event.clientY - (rect.y - bodyRect.top);
    setDragOffset({ x: offsetX, y: offsetY });

    cloneEl.style.position = 'absolute';
    cloneEl.style.pointerEvents = 'none';
    cloneEl.style.userSelect = 'none';
    cloneEl.style.zIndex = '999999';
    cloneEl.style.left = '-10000px';
    cloneEl.style.top = '-10000px';
    cloneEl.style.opacity = '0.6';
    cloneEl.style.width = dragPage.getBoundingClientRect().width + 'px';
    cloneEl.id = 'currentTreeItemDragElement';
    setCurrentDragGhost(cloneEl);
    document.body.appendChild(cloneEl);
  };

  const handleDragEnd = () => {
    setDraggingKey('');
    if (!currentDragGhost) return;
    currentDragGhost.remove();
    setCurrentDragGhost(undefined);
  };

  useEffect(() => {
    const handleDrag = (e: any) => {
      if (!currentDragGhost) return;
      const setPosition = (value: number) => value || -10000;
      currentDragGhost.style.left = setPosition(e.clientX) - dragOffset.x + 'px';
      currentDragGhost.style.top = setPosition(e.clientY) - dragOffset.y + 'px';
    };

    window.addEventListener('drag', handleDrag);
    return () => {
      window.removeEventListener('drag', handleDrag);
    };
  }, [currentDragGhost]);

  useEffect(() => {
    const convertedTree = convertToAntdTree(pages);
    setTreeData(convertedTree);
    setExpandedKeys(getExpandedKeys(convertedTree));
  }, [pages]);

  return (
    <div style={{ maxHeight: height, paddingTop: 3 }}>
      <Tree
        treeData={treeData}
        draggable={{ icon: false }}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onExpand={(e, info) => handleExpandItem(info)}
        onDrop={handleDrop}
        blockNode
        switcherIcon={({ expanded, data }) => {
          return (
            <IconButton size='small' style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(180deg)', marginBottom: 6, viewTransitionName: `page-switch-${data.key}` }}>
              <ExpandLessIcon />
            </IconButton>
          );
        }}
        expandedKeys={expandedKeys}
        titleRender={(node) => {
          if (node.key === '__drop_spacer__') {
            return (
              <div
                style={{
                  height: 50,
                  marginTop: 0,
                  backgroundColor: 'transparent',
                  cursor: 'default',
                }}
              />
            );
          }
          return (
            <CoursePageItem
              nodeData={node as IPageItem}
              onExpand={handleExpand}
              handleNewPageItem={handleNewPageItem}
              handlePastePage={handlePastePage}
              handleCopyPageItem={handleCopyPageItem}
              handleDuplicatePageItem={handleDuplicatePageItem}
              draggingKey={draggingKey}
              expandedKeys={expandedKeys}
              image={image}
            />
          );
        }}
        rootStyle={{ backgroundColor: 'transparent' }}
      />
    </div>
  );
});
